import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { PageHeader, Card, StatusPill } from '@/components/Primitives';
import { Plug, DownloadSimple, Eye } from '@phosphor-icons/react';
import { useToast } from '@/hooks/use-toast';

const Fayn = () => {
    const [s, setS] = useState(null);
    const [sipPwd, setSipPwd] = useState('');
    const [template, setTemplate] = useState('');
    const [mode, setMode] = useState('');
    const [testing, setTesting] = useState(false);
    const [rendered, setRendered] = useState(null);
    const { toast } = useToast();

    const load = async () => {
        const [a, t] = await Promise.all([
            api.get('/integrations/fayn/settings'),
            api.get('/integrations/fayn/trunk-template'),
        ]);
        setS(a.data);
        setTemplate(t.data.template);
        setMode(t.data.mode);
    };
    useEffect(() => { load(); }, []);

    const save = async (e) => {
        e.preventDefault();
        try {
            const payload = { ...s };
            ['api_key_set','sip_password_set','last_connection_test_at',
             'last_connection_test_ok','last_connection_test_message',
             'updated_at','id'].forEach(k => delete payload[k]);
            if (sipPwd) payload.sip_register_password = sipPwd;
            await api.put('/integrations/fayn/settings', payload);
            setSipPwd('');
            load();
            toast({ title: 'Uloženo' });
        } catch (err) {
            toast({ title: 'Chyba', description: err.response?.data?.detail || 'error', variant: 'destructive' });
        }
    };

    const testConn = async () => {
        setTesting(true);
        try {
            const r = await api.post('/integrations/fayn/test-connection');
            toast({
                title: r.data.ok ? 'Spojení OK' : 'Spojení selhalo',
                description: r.data.message,
                variant: r.data.ok ? 'default' : 'destructive',
            });
            load();
        } finally {
            setTesting(false);
        }
    };

    const showRendered = async () => {
        try {
            const r = await api.get('/integrations/fayn/rendered-config');
            setRendered(r.data.content);
        } catch (err) {
            toast({ title: 'Chyba', description: err.response?.data?.detail || 'error', variant: 'destructive' });
        }
    };

    if (!s) return <div>Načítání…</div>;

    return (
        <>
            <PageHeader
                eyebrow="Trunk Provider"
                title="Fayn.cz SIP Trunk"
                description="Druhý SIP trunk poskytovatel vedle DIDWW — podporuje SIP Registration mode (PBX se registruje na sip.fayn.cz) s CLIP / CLIR."
                actions={
                    <button onClick={testConn} disabled={testing}
                            data-testid="fayn-test-btn"
                            className="inline-flex items-center gap-2 px-3 py-2 text-xs font-bold uppercase tracking-[0.08em] border"
                            style={{ borderColor: 'var(--border-default)', borderRadius: 2 }}>
                        <Plug size={14} weight="bold" />
                        {testing ? 'Testuji…' : 'Test Yeastar API'}
                    </button>
                }
            />

            <form onSubmit={save} className="space-y-4">
                <Card title="SIP Registration mode (Fayn.cz)">
                    <div className="text-[10px] uppercase tracking-[0.1em] font-bold mb-3"
                         style={{ color: 'var(--brand)' }}>
                        Mode: {mode || 'static'} · Heslo {s.sip_password_set ? '✓ uloženo' : '✗ nezadáno'}
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                        <label className="inline-flex items-center gap-2 text-sm col-span-3">
                            <input type="checkbox" checked={!!s.sip_register_enabled}
                                   data-testid="fayn-sip-reg-enabled"
                                   onChange={(e) => setS({ ...s, sip_register_enabled: e.target.checked })} />
                            Aktivovat SIP REGISTRATION (PBX se registruje k Fayn)
                        </label>
                        <div>
                            <label className="text-[10px] uppercase font-bold" style={{ color: 'var(--text-secondary)' }}>SIP Username</label>
                            <input value={s.sip_register_username || ''}
                                   onChange={(e) => setS({ ...s, sip_register_username: e.target.value })}
                                   placeholder="312310095"
                                   data-testid="fayn-sip-user"
                                   className="w-full mt-1 px-2 py-1.5 bg-[var(--bg-app)] border text-sm font-mono"
                                   style={{ borderColor: 'var(--border-default)', borderRadius: 2 }} />
                        </div>
                        <div>
                            <label className="text-[10px] uppercase font-bold" style={{ color: 'var(--text-secondary)' }}>SIP Heslo</label>
                            <input type="password" value={sipPwd} onChange={(e) => setSipPwd(e.target.value)}
                                   placeholder={s.sip_password_set ? '••••••••' : 'Hf8N11v5E'}
                                   data-testid="fayn-sip-password"
                                   className="w-full mt-1 px-2 py-1.5 bg-[var(--bg-app)] border text-sm font-mono"
                                   style={{ borderColor: 'var(--border-default)', borderRadius: 2 }} />
                        </div>
                        <div>
                            <label className="text-[10px] uppercase font-bold" style={{ color: 'var(--text-secondary)' }}>DID Number</label>
                            <input value={s.did_number || ''}
                                   onChange={(e) => setS({ ...s, did_number: e.target.value })}
                                   placeholder="420312310095"
                                   className="w-full mt-1 px-2 py-1.5 bg-[var(--bg-app)] border text-sm font-mono"
                                   style={{ borderColor: 'var(--border-default)', borderRadius: 2 }} />
                        </div>
                        <div>
                            <label className="text-[10px] uppercase font-bold" style={{ color: 'var(--text-secondary)' }}>CLIP (Caller ID Number)</label>
                            <input value={s.caller_id_number || ''}
                                   onChange={(e) => setS({ ...s, caller_id_number: e.target.value })}
                                   placeholder="420312310095"
                                   className="w-full mt-1 px-2 py-1.5 bg-[var(--bg-app)] border text-sm font-mono"
                                   style={{ borderColor: 'var(--border-default)', borderRadius: 2 }} />
                        </div>
                        <label className="inline-flex items-center gap-2 text-sm mt-6">
                            <input type="checkbox" checked={!!s.clir_enabled}
                                   data-testid="fayn-clir"
                                   onChange={(e) => setS({ ...s, clir_enabled: e.target.checked })} />
                            CLIR (skrýt číslo volaného)
                        </label>
                        <div>
                            <label className="text-[10px] uppercase font-bold" style={{ color: 'var(--text-secondary)' }}>SIP Proxy Host</label>
                            <input value={s.sip_proxy_host || 'sip.fayn.cz'}
                                   onChange={(e) => setS({ ...s, sip_proxy_host: e.target.value })}
                                   className="w-full mt-1 px-2 py-1.5 bg-[var(--bg-app)] border text-sm font-mono"
                                   style={{ borderColor: 'var(--border-default)', borderRadius: 2 }} />
                        </div>
                        <div>
                            <label className="text-[10px] uppercase font-bold" style={{ color: 'var(--text-secondary)' }}>Port</label>
                            <input type="number" value={s.sip_proxy_port || 5060}
                                   onChange={(e) => setS({ ...s, sip_proxy_port: parseInt(e.target.value, 10) })}
                                   className="w-full mt-1 px-2 py-1.5 bg-[var(--bg-app)] border text-sm font-mono"
                                   style={{ borderColor: 'var(--border-default)', borderRadius: 2 }} />
                        </div>
                        <div>
                            <label className="text-[10px] uppercase font-bold" style={{ color: 'var(--text-secondary)' }}>Transport</label>
                            <select value={s.transport || 'udp'} onChange={(e) => setS({ ...s, transport: e.target.value })}
                                    className="w-full mt-1 px-2 py-1.5 bg-[var(--bg-app)] border text-sm"
                                    style={{ borderColor: 'var(--border-default)', borderRadius: 2 }}>
                                <option value="udp">UDP</option>
                                <option value="tcp">TCP</option>
                                <option value="tls">TLS</option>
                            </select>
                        </div>
                    </div>
                </Card>

                <Card title="PJSIP trunk template (preview)" action={
                    <button type="button" onClick={showRendered}
                            data-testid="fayn-show-rendered"
                            className="inline-flex items-center gap-2 px-2 py-1 text-[10px] font-bold uppercase border"
                            style={{ borderColor: 'var(--border-default)', borderRadius: 2 }}>
                        <Eye size={12} weight="bold" />Zobrazit s heslem
                    </button>
                }>
                    <pre className="px-3 py-2 font-mono text-[11px] overflow-x-auto max-h-80"
                         style={{ background: '#06080A', borderRadius: 2 }}>{template}</pre>
                </Card>

                <div className="flex gap-2">
                    <button type="submit" data-testid="fayn-save-btn"
                            className="px-4 py-2 text-xs font-bold uppercase tracking-[0.08em]"
                            style={{ background: 'var(--brand)', color: '#fff', borderRadius: 2 }}>
                        Uložit nastavení
                    </button>
                </div>
            </form>

            {rendered && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
                     style={{ background: 'rgba(11,14,20,0.85)' }}>
                    <div className="w-full max-w-4xl border p-6 max-h-[90vh] overflow-y-auto"
                         style={{ background: 'var(--bg-surface)', borderColor: 'var(--border-default)', borderRadius: 2 }}>
                        <div className="flex justify-between items-start mb-4">
                            <h2 className="font-heading text-xl font-bold">Rendered Fayn config (s reálným heslem)</h2>
                            <button onClick={() => setRendered(null)} className="text-2xl">×</button>
                        </div>
                        <p className="text-xs mb-3" style={{ color: 'var(--text-secondary)' }}>
                            ⚠️ Zobrazuje se SIP heslo v plain textu. Po zkopírování do Asterisk pjsip.conf okno zavřete.
                        </p>
                        <pre className="px-3 py-2 font-mono text-[11px] overflow-x-auto"
                             style={{ background: '#06080A', borderRadius: 2 }}>{rendered}</pre>
                        <button onClick={() => navigator.clipboard.writeText(rendered)}
                                className="mt-3 px-3 py-2 text-xs font-bold uppercase"
                                style={{ background: 'var(--brand)', color: '#fff', borderRadius: 2 }}>
                            Zkopírovat do schránky
                        </button>
                    </div>
                </div>
            )}
        </>
    );
};

export default Fayn;
